import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Col } from 'reactstrap';
import moment from 'moment';
import { environment } from '../../../../environment/environment';
import apiResponseHandler from '../../../../utils/apiResponseHandler';
import { IEmployeeSegmentAssignment } from '../../../../types/SegmentationBlockTypes';
import './EmployeeSegmentationBlocks.css';

interface EmployeeSegmentationBlocksProps {
  employeeId: string;
}

const EmployeeSegmentationBlocks: React.FC<EmployeeSegmentationBlocksProps> = ({
  employeeId,
}) => {
  const [assignments, setAssignments] = useState<IEmployeeSegmentAssignment[]>(
    []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchAssignments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const fetchAssignments = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${environment.baseUrl}/segment-assignments/for-employee/${employeeId}/`
      );
      const all = Array.isArray(response.data?.results) ? response.data.results : [];
      setAssignments(all.filter((a: IEmployeeSegmentAssignment) => a.feature_type === 'EWA_BLOCKING'));
    } catch (err) {
      apiResponseHandler.handleApiError('Error', err);
      setError('Failed to load segmentation blocks');
    } finally {
      setLoading(false);
    }
  };

  const activeBlocks = assignments.filter((a) => a.is_active);
  const inactiveBlocks = assignments.filter((a) => !a.is_active);

  if (loading) {
    return (
      <Col className="d-flex justify-content-center mt-3">
        <div className="spinner-border" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </Col>
    );
  }

  if (error) {
    return (
      <div className="segmentation-blocks-error">
        <p>{error}</p>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={fetchAssignments}
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="segmentation-blocks-container">
      {assignments.length === 0 ? (
        <div className="segmentation-blocks-empty">
          <p className="segmentation-blocks-empty-title">
            No segmentation blocks
          </p>
          <p className="segmentation-blocks-empty-description">
            This employee is not affected by any segmentation blocks.
          </p>
        </div>
      ) : (
        <>
          {activeBlocks.length > 0 && (
            <div className="segmentation-blocks-section">
              <h3 className="segmentation-blocks-section-title">
                Active Blocks ({activeBlocks.length})
              </h3>
              {activeBlocks.map((assignment) => (
                <div
                  key={assignment.id}
                  className="segmentation-block-card segmentation-block-card--active"
                >
                  <h4 className="segmentation-block-title">
                    {assignment.name}
                  </h4>
                  <p className="segmentation-block-date">
                    Created {moment(assignment.created_at).format('MMM D, YYYY')}
                  </p>
                </div>
              ))}
            </div>
          )}

          {inactiveBlocks.length > 0 && (
            <div className="segmentation-blocks-section">
              <h3 className="segmentation-blocks-section-title">
                Inactive Blocks ({inactiveBlocks.length})
              </h3>
              {inactiveBlocks.map((assignment) => (
                <div
                  key={assignment.id}
                  className="segmentation-block-card segmentation-block-card--inactive"
                >
                  <h4 className="segmentation-block-title">
                    {assignment.name}
                  </h4>
                  <p className="segmentation-block-date">
                    Created {moment(assignment.created_at).format('MMM D, YYYY')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmployeeSegmentationBlocks;
